import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWorkspaceNavigate } from '../../workspace-path';
import {
  contractSchema,
  metafieldSchema,
  type Contract,
  type Metafield,
  type Project,
} from '@forecastify/shared';
import { Toaster, type Toast } from '@forecastify/ui';
import { useT } from '../../i18n';
import { CONTRACT_TOPICS } from '../../live/topics';
import { useLiveInvalidation } from '../../live/use-live';
import { useSession } from '../../session';
import { managesProject, parseList, useMyMemberId, usePermissionSet } from '../permissions-hook';
import { requestAll } from '../read-all';
import { ContractDialog, draftRecurrence, type ContractDraft } from './contract-dialog';
import { ContractList, contractErrorKey } from './contracts-screen';
import { contractLabel } from './contract-label';
import { AttachContractDialog } from './attach-contract-dialog';
import { bulkContractStatus } from './bulk-status';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The project's Szerződések tab — `2fazis.main.jsx` 6053 and 4068–4100.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THIS IS WHERE PEOPLE LOOK FIRST, AND IT WAS NOT THERE.** `pTabs` has carried
 * *Dashboard · Feladatok · Idő · **Szerződések** · Emberek* since the phase-2 prototype was
 * written, with a count on it; C24 built the workspace-wide screen and stopped. A contract belongs
 * to exactly one project (`contracts.project_id` is `not null`), so the project is the natural
 * place to ask what it has agreed to — and answering only from a separate top-level list makes the
 * relationship invisible from the side that owns it.
 *
 * ⚠️ **The same two lists as `/contracts`, narrowed** — `pcMode` is the mockup's own second mode
 * switch, and it lives in the tab rather than the page header because the page header here belongs
 * to the project.
 *
 * ⚠️⚠️ **A ROW OPENS `/contracts/:code`, IT DOES NOT OPEN A DRAWER HERE.** The tri-modal surface
 * belongs to the contracts route, which owns the fetches that feed it; duplicating it would mean a
 * second copy of the history read, the attachment upload and the line-item dialog. A line item's
 * row opens its CONTRACT for the same reason plus one more — a line item still has no address of
 * its own (`decisions.md`, *Still open*).
 *
 * ⚠️ **Live on `CONTRACT_TOPICS`**, like the route it borrows from: the numbers on both lists are
 * joins through tasks and worklogs, so a colleague logging an hour changes a row here.
 */
export function ProjectContractsTab({
  project,
  startWithNew = false,
}: {
  readonly project: Project;
  /** P2-32 · the project gate's *Új szerződés*: the tab opens with the create dialog open. */
  readonly startWithNew?: boolean;
}) {
  const t = useT();
  const session = useSession();
  /* ⚠️ R-38 · `go`, not `navigate`: it takes a WORKSPACE-RELATIVE path and supplies the first
     segment. The name is what lets `eslint.config.mjs` ban a bare `go('/tasks')`,
     which under R-38 names a workspace rather than a screen. */
  const go = useWorkspaceNavigate();
  const permissions = usePermissionSet();

  /* 31 Oct 2026 · the attach dialog's — the workspace's contracts with no project. The tab's own
     list pages on the server (`ContractList`) and re-reads on `nonce`. */
  const [free, setFree] = useState<readonly Contract[]>([]);
  const [listed, setListed] = useState<readonly Contract[]>([]);
  const [nonce, setNonce] = useState(0);
  const [newOpen, setNewOpen] = useState(startWithNew);
  /* C56 · the other half of the split button. */
  const [attachOpen, setAttachOpen] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  /* C30 · the workspace's `contract` fields, so the create dialog can ask for them here too. */
  const [fields, setFields] = useState<readonly Metafield[]>([]);
  /* The answer a bulk status change gets — `bulk-status.ts`. */
  const [toasts, setToasts] = useState<readonly Toast[]>([]);

  /**
   * The attach dialog's contracts. ⚠️ `/line-items` is no longer read here — the flat list it fed
   * is gone, and the tab was fetching every line item in the workspace on every visit to filter it
   * down to one project's. Line items are read inside the contract that owns them.
   */
  const readFree = useMemo(
    () => async (): Promise<void> => {
      if (session.workspaceId === null) return;
      /* ⚠️ A 403 is the plan boundary and reads as empty — the tab is only rendered when the flag
         is on, but a plan that changed this morning must not throw an error screen at a project. */
      const contractList = await requestAll(session, '/contracts?scope=none').catch(() => null);
      if (contractList?.ok === true) {
        setFree(parseList(contractSchema, await contractList.json()) ?? []);
      }
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug, `preferences-boot.tsx`); depending on it refetches for ever. */
    [session.workspaceId],
  );

  useEffect(() => {
    void readFree();
  }, [readFree]);

  /* After a write or a live change: the list's page (`nonce`) and the attachable ones. */
  const reload = useCallback(async (): Promise<void> => {
    setNonce((current) => current + 1);
    await readFree();
  }, [readFree]);

  /* ⚠️ Non-fatal, like the lists: no fields means no section, which is what most workspaces see. */
  useEffect(() => {
    if (session.workspaceId === null) return;
    void (async () => {
      const response = await session.request('/metafields?entity=contract').catch(() => null);
      if (response?.ok !== true) return;
      setFields(parseList(metafieldSchema, await response.json()) ?? []);
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug, `preferences-boot.tsx`); depending on it refetches for ever. */
  }, [session.workspaceId]);

  useLiveInvalidation(CONTRACT_TOPICS, () => {
    void reload();
  });

  /**
   * C56 · ⚠️⚠️ **`POST /contracts/:id/project`, NOT A PATCH.** Attaching mints a code and
   * detaching is guarded, so they are verbs rather than a nullable field — `contract.service.ts`
   * carries the argument. The dialog stays open on a refusal, with the sentence in it.
   */
  const attachContract = (contract: Contract): void => {
    void (async () => {
      setServerError(null);
      const response = await session
        .request(`/contracts/${contract.id}/project`, {
          method: 'POST',
          body: JSON.stringify({ projectId: project.id, version: contract.version }),
        })
        .catch(() => null);

      if (response?.ok !== true) {
        const envelope = (await response?.json().catch(() => null)) as
          { error?: { code?: string } } | null | undefined;
        setServerError(envelope?.error?.code ?? 'unavailable');
        return;
      }
      setAttachOpen(false);
      await reload();
    })();
  };

  const create = (draft: ContractDraft): void => {
    const memberId = session.workspaces.find(
      (workspace) => workspace.id === session.workspaceId,
    )?.memberId;
    if (memberId === undefined) return;

    void (async () => {
      setServerError(null);
      const response = await session
        .request('/contracts', {
          method: 'POST',
          body: JSON.stringify({
            name: draft.name.trim(),
            projectId: draft.projectId,
            type: draft.type,
            startsOn: draft.startsOn,
            endsOn: draft.endsOn === '' ? null : draft.endsOn,
            ownerMemberId: memberId,
            ...(Object.keys(draft.metafields).length === 0 ? {} : { metafields: draft.metafields }),
            recurrence: draft.type === 'recurring' ? draftRecurrence(draft) : null,
          }),
        })
        .catch(() => null);

      if (response?.ok !== true) {
        const envelope = (await response?.json().catch(() => null)) as
          { error?: { code?: string } } | null | undefined;
        setServerError(envelope?.error?.code ?? 'unavailable');
        return;
      }
      setNewOpen(false);
      await reload();
    })();
  };

  /* 22 Oct 2026 · at `own`, only on a project the reader is PM of (`project-manage.ts`). */
  const myMemberId = useMyMemberId();
  const canManage = managesProject(permissions, 'contract.manage', project.pmMemberId, myMemberId);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/**
       * ┌───────────────────────────────────────────────────────────────────┐
       * │ ⚠️⚠️ THE LINE-ITEM VIEW IS GONE FROM HERE — 14 Sep.               │
       * └───────────────────────────────────────────────────────────────────┘
       *
       * A dropdown switched this tab between the project's contracts and a flat list of its line
       * items. A line item only means anything under the contract that owns it — its code, its
       * estimate and its status are all relative to that — so a flat list invited somebody to read
       * one out of context, and the row's only action was to navigate to the contract anyway.
       *
       * ⚠️ **Line items are reached from inside a contract and nowhere else now.** The tab has one
       * job again, so the heading the dropdown carried is gone too rather than becoming a label
       * for a list that no longer has an alternative.
       */}

      {serverError === null ? null : (
        <p className="text-destructive border-b px-5 py-2 text-[12px]" role="alert">
          {t(contractErrorKey(serverError))}
        </p>
      )}

      <div className="min-h-0 flex-1">
        {
          <ContractList
            projectId={project.id}
            nonce={nonce}
            onRows={setListed}

            onOpen={(contract) => {
              go(`/contracts/${contractLabel(contract)}`);
            }}
            {...(canManage
              ? {
                  onNew: () => {
                    setNewOpen(true);
                  },
                  /* ⚠️ C56 · its presence is what turns the button into the mockup's split menu. */
                  onAttach: () => {
                    setAttachOpen(true);
                  },
                  onBulkStatus: (ids, status) => {
                    void (async () => {
                      const toast = await bulkContractStatus(
                        session,
                        listed,
                        ids,
                        status,
                        reload,
                        t,
                      );
                      setToasts((current) => [...current, toast]);
                    })();
                  },
                }
              : {})}
          />
        }
      </div>

      {canManage ? (
        <AttachContractDialog
          open={attachOpen}
          /* ⚠️ The project-less ones (`?scope=none`) — the dialog narrows to those itself, and a
             list already filtered to this project would be empty by construction. */
          contracts={free}
          projectClientId={project.clientId}
          serverError={serverError === null ? null : t(contractErrorKey(serverError))}
          onClose={() => {
            setAttachOpen(false);
          }}
          onSubmit={attachContract}
        />
      ) : null}

      {/* ⚠️ **The project is decided and the field is locked** — a create started from a project's
          own tab that could then pick a different project would be a trap rather than a shortcut. */}
      {canManage ? (
        <ContractDialog
          open={newOpen}
          projects={[project]}
          /* ⚠️ C56 · empty, and the switch is not drawn: `defaultProjectId` already decides
             the scope, so there is no choice here for a client list to serve. */
          clients={[]}
          workFreeRevenue={false}
          defaultProjectId={project.id}
          metafields={fields}
          /* ⚠️ Empty: the `user` metafield type needs a member list this tab does not read, and the
             contracts screen is where a workspace using one would create from. */
          members={[]}
          serverError={serverError === null ? null : t(contractErrorKey(serverError))}
          onClose={() => {
            setNewOpen(false);
          }}
          onSubmit={create}
        />
      ) : null}

      <Toaster
        toasts={[...toasts]}
        label={t('projects.toasts')}
        dismissLabel={t('common.close')}
        onDismiss={(id) => {
          setToasts((current) => current.filter((entry) => entry.id !== id));
        }}
      />
    </div>
  );
}
